import type { Router } from 'vue-router'
import { getActivePinia } from 'pinia'
import { createGtm, useGtm, type TrackEventOptions } from '@gtm-support/vue-gtm'

import { GtmEvent } from '@/types/gtm'

export default defineNuxtPlugin({
  parallel: true,
  setup: nuxtApp => {
    const config = useRuntimeConfig()
    const isProd = config.public.configEnv === 'prod'

    const gtmId = config.public.gtmId

    let trackEvent: (data: GtmEvent | TrackEventOptions) => void = () => {}

    if (gtmId) {
      nuxtApp.vueApp.use(
        createGtm({
          id: config.public.gtmId,
          defer: true,
          compatibility: false,
          enabled: true,
          debug: !isProd,
          loadScript: true,
          vueRouter: useRouter() as Router,
          trackOnNextTick: false,
        })
      )
      trackEvent = data => {
        const store = getActivePinia()
        const gtm = useGtm()
        const eventPayload = typeof data === 'string' ? { event: data } : data
        gtm?.trackEvent({ store: null, payload: null })
        gtm?.trackEvent({ ...eventPayload, store: store?.state.value })
      }
    }

    // setup gtm directive
    nuxtApp.vueApp.directive('gtm', {
      mounted(el, binding) {
        const { arg: domEvent, value: gtmEvent, modifiers } = binding
        const domEventOptions = {
          once: modifiers.once ?? true,
          passive: modifiers.passive,
          capture: modifiers.capture,
        }
        if (domEvent) {
          el.addEventListener(
            domEvent,
            () => {
              trackEvent(gtmEvent)
            },
            domEventOptions
          )
        } else {
          trackEvent(gtmEvent)
        }
      },
    })

    if (import.meta.client) {
      // setup gtm dataLayer
      window.dataLayer ||= []

      // expose GtmEvent to window
      window.EtgGtmEvt = GtmEvent
    }

    // provide global trackEvent function
    return {
      provide: {
        trackEvent,
      },
    }
  },
})
